import { useId } from 'react';
import {
  ARCH_LOWER,
  ARCH_UPPER,
  SECTIONS,
  SPAN_KIND_LABEL,
  archOf,
  sortTeeth,
  spanCovers,
  toothGlyphOf,
  type Arch,
  type Section,
  type Span,
  type ToothGlyph,
  type ToothNumber,
} from '@/domain';
import { strings } from '@/shared/strings';
import { usePhoneLayout } from '@/ui/layout/usePhoneLayout';
import styles from './Odontogram.module.css';

export type OdontogramSize = 'picker' | 'compact' | 'print';

export interface OdontogramProps {
  selected: string[];
  onChange?: (teeth: string[]) => void;
  readOnly?: boolean;
  /** picker on intake; compact on the job view, Final Check and the record; print on the sheet. */
  size?: OdontogramSize;
  spans?: readonly Span[];
  /** MOLAR / PREMOLAR / CANINE / INCISORS above and below the arches. Off on the sheet. */
  typeNames?: boolean;
  /** Shows the "3 teeth selected" caption under the chart. */
  showSelection?: boolean;
}

/*
 * The four section chart.
 *
 * Not a mouth. The client asked for the chart a dentist uses: four sections,
 * upper right, upper left, lower right, lower left, each tooth drawn by its
 * type in a row and numbered in FDI. One SVG per arch on a desktop, one per
 * section on a phone, and every measurement below is what the Figma
 * components carry ("Odontogram / four sections"). Where this file and Figma
 * disagree, Figma is the reference.
 *
 * Bridges and titanium bars are drawn ABOVE the teeth on both arches, on two
 * levels: the bridge nearest the teeth and the bar above it, so the two can
 * cover the same teeth. The levels are always laid out, bar or no bar, so the
 * chart is the same height on every case.
 */

/** Desktop: the cell each tooth sits in, the gap at the midline, the padding. */
const CELL = 34;
const MIDLINE_GAP = 24;
const SIDE = 16;
const ARCH_WIDTH = SIDE * 2 + CELL * 16 + MIDLINE_GAP; // 600

/** The phone: eight cells of 38 with 3px each side, the chart wrap's inner width. */
const ROW_CELL = 38;
const ROW_SIDE = 3;
const ROW_WIDTH = ROW_SIDE * 2 + ROW_CELL * 8; // 310

/** The glyph box, and how far a span stops inside its end cells. */
const GLYPH_WIDTH = 24;
const GLYPH_HEIGHT = 52;
const SPAN_INSET = 3;
const BRIDGE_TICK = 8;
const BRACKET_TICK = 5;

type Band =
  | 'typeNames'
  | 'typeBrackets'
  | 'barLabel'
  | 'barLine'
  | 'bridgeLabel'
  | 'bridgeLine'
  | 'ticks'
  | 'teeth'
  | 'numbers'
  | 'sectionBrackets'
  | 'sectionLabels';

const BAND_HEIGHT: Record<Band, number> = {
  typeNames: 12,
  typeBrackets: 5,
  barLabel: 12,
  barLine: 4,
  bridgeLabel: 12,
  bridgeLine: 2,
  ticks: 8,
  teeth: GLYPH_HEIGHT,
  numbers: 14,
  sectionBrackets: 5,
  sectionLabels: 12,
};

/**
 * The bands sit flush inside a group, and the groups are 6 apart. That is what
 * makes an arch 168 tall, the height the Figma component carries: type names
 * and brackets 17, bar level 16, bridge level 22, teeth 52, numbers 14,
 * section brackets and labels 17, and five gaps of 6.
 */
const GROUP_GAP = 6;
type Group = readonly Band[];

/**
 * Top to bottom, per arch. The type names sit above the upper arch and below
 * the lower one, so the two stacks are the same groups in a different order.
 * The spans stay above the teeth on both.
 */
const UPPER_GROUPS: readonly Group[] = [
  ['typeNames', 'typeBrackets'],
  ['barLabel', 'barLine'],
  ['bridgeLabel', 'bridgeLine', 'ticks'],
  ['teeth'],
  ['numbers'],
  ['sectionBrackets', 'sectionLabels'],
];

const LOWER_GROUPS: readonly Group[] = [
  ['barLabel', 'barLine'],
  ['bridgeLabel', 'bridgeLine', 'ticks'],
  ['teeth'],
  ['numbers'],
  ['sectionBrackets', 'sectionLabels'],
  ['typeBrackets', 'typeNames'],
];

/** One row of the phone layout: the section label is HTML above the SVG. */
const ROW_GROUPS: readonly Group[] = [
  ['barLabel', 'barLine'],
  ['bridgeLabel', 'bridgeLine', 'ticks'],
  ['teeth'],
  ['numbers'],
];

interface Layout {
  top: Partial<Record<Band, number>>;
  height: number;
}

function layoutOf(groups: readonly Group[], typeNames: boolean): Layout {
  const top: Partial<Record<Band, number>> = {};
  let y = 0;
  let first = true;
  for (const group of groups) {
    const bands = group.filter(
      (band) => typeNames || (band !== 'typeNames' && band !== 'typeBrackets'),
    );
    if (bands.length === 0) continue;
    if (!first) y += GROUP_GAP;
    first = false;
    for (const band of bands) {
      top[band] = y;
      y += BAND_HEIGHT[band];
    }
  }
  return { top, height: y };
}

/** A text baseline that sits at the foot of its band. */
function baseline(layout: Layout, band: Band): number {
  return (layout.top[band] ?? 0) + BAND_HEIGHT[band] - 2;
}

/*
 * The five silhouettes, crown down, in a 24 by 52 box. These are the paths
 * the Figma ToothGlyph component carries; the lower arch draws the same
 * glyph turned 180 degrees so its crowns face the upper arch.
 */
const GLYPH: Record<ToothGlyph, { roots: string; crown: string; fissure: string | null }> = {
  molar: {
    roots: 'M5 29 L6.2 5 Q7.6 1.5 9 5 L10.8 29 Z M13.2 29 L15 5 Q16.4 1.5 17.8 5 L19 29 Z',
    crown: 'M3 29 Q3 24 7 24 L17 24 Q21 24 21 29 L21 44 Q21 51 12 51 Q3 51 3 44 Z',
    fissure: 'M8 39 H16 M12 35 V43',
  },
  premolar: {
    roots: 'M8.2 29 L10.4 5 Q12 1.5 13.6 5 L15.8 29 Z',
    crown: 'M5 29 Q5 24 8.5 24 L15.5 24 Q19 24 19 29 L19 44 Q19 51 12 51 Q5 51 5 44 Z',
    fissure: 'M9 39 H15',
  },
  canine: {
    roots: 'M8.5 29 L10.6 3.5 Q12 0.5 13.4 3.5 L15.5 29 Z',
    crown: 'M6 29 Q6 24 9 24 L15 24 Q18 24 18 29 L18 40 L12 51 L6 40 Z',
    fissure: null,
  },
  central: {
    roots: 'M8.2 29 L10.4 5 Q12 1.5 13.6 5 L15.8 29 Z',
    crown:
      'M4.5 29 Q4.5 24 8 24 L16 24 Q19.5 24 19.5 29 L19 47.5 Q19 51 15.5 51 L8.5 51 Q5 51 5 47.5 Z',
    fissure: null,
  },
  lateral: {
    roots: 'M8.2 29 L10.4 5 Q12 1.5 13.6 5 L15.8 29 Z',
    crown:
      'M6 29 Q6 24 9 24 L15 24 Q18 24 18 29 L17.5 47.5 Q17.5 51 14.5 51 L9.5 51 Q6.5 51 6.5 47.5 Z',
    fissure: null,
  },
};

/** The type groups of one section, in the order the section runs. */
const TYPE_GROUPS: { label: string; glyphs: ToothGlyph[] }[] = [
  { label: strings.odontogram.molar, glyphs: ['molar'] },
  { label: strings.odontogram.premolar, glyphs: ['premolar'] },
  { label: strings.odontogram.canine, glyphs: ['canine'] },
  { label: strings.odontogram.incisors, glyphs: ['central', 'lateral'] },
];

/**
 * The viewport rule the four-row layout follows, `usePhoneLayout` in ui/layout,
 * and the ONE place that still uses it: the intake picker.
 *
 * Section 10 of the request of 20 September 2026 supersedes section 4.5 of the
 * chart document for every READ-ONLY screen. The client has seen the four rows of
 * eight and wants the whole mouth in one view on a phone, as the sheet prints it,
 * on the job view, Final Check, the record and the order drawer. At 390px the
 * teeth are about 19px wide, which is fine for reading, and reading is all those
 * screens do.
 *
 * The picker keeps the four rows, and that is the one deviation from the client's
 * words, flagged in section 10.3 of the request so they can overrule it: a 19px
 * tooth cannot be tapped reliably, the picker exists to be tapped, and reception
 * books cases in at a desk. Making the picker full width too needs a zoom step,
 * which is a different piece of work.
 */
export function Odontogram({
  selected,
  onChange,
  readOnly = false,
  size = 'picker',
  spans = [],
  typeNames = size !== 'print',
  showSelection = false,
}: OdontogramProps) {
  const titleId = useId();
  const pickable = !readOnly && onChange !== undefined;
  const chosen = new Set(selected.map(String));
  // Only where a tooth is tapped. Everything read-only draws the whole mouth.
  // jsdom and the print pipeline have no matchMedia; both draw the desktop chart.
  const phoneScreen = usePhoneLayout();
  const phone = pickable && phoneScreen;

  function toggle(tooth: string) {
    if (!pickable) return;
    const next = new Set(chosen);
    if (next.has(tooth)) next.delete(tooth);
    else next.add(tooth);
    onChange(sortTeeth([...next]));
  }

  const title =
    selected.length === 0
      ? strings.odontogram.emptyLabel
      : `${strings.odontogram.chartLabel}: ${sortTeeth(selected).join(', ')}`;

  const sizeClass = size === 'compact' ? styles.compact : size === 'print' ? styles.print : '';
  const drawn = spans.filter((span) => archOf(span.from as ToothNumber) === archOf(span.to as ToothNumber));

  const shared = { chosen, pickable, onToggle: toggle, spans: drawn, typeNames };

  const chart = (
    <div
      className={`${styles.chart} ${sizeClass} ${pickable ? styles.pickable : ''}`}
      role="group"
      aria-labelledby={titleId}
    >
      <span id={titleId} className={styles.srOnly}>
        {title}
      </span>

      {phone ? (
        <div className={styles.rows}>
          {SECTIONS.map((section) => (
            <SectionRow key={section.key} section={section} {...shared} />
          ))}
        </div>
      ) : (
        <>
          <ArchSvg arch="upper" {...shared} />
          <ArchSvg arch="lower" {...shared} />
        </>
      )}

      {size !== 'print' && (
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.legendBridge} /> {strings.odontogram.bridge}
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendBar} /> {strings.odontogram.bar}
          </span>
          {pickable && <span className={styles.legendHelp}>{strings.odontogram.help}</span>}
        </div>
      )}
    </div>
  );

  if (size === 'print') return chart;

  return (
    <div>
      <div className={styles.wrap}>{chart}</div>
      {showSelection && <SelectionCaption selected={selected} />}
    </div>
  );
}

/**
 * The caption under the chart: which teeth are marked, and how many.
 *
 * A component of its own since the request of 5 October 2026 (third), section
 * 5. The enlarged chart is scaled to fit its screen and turned on its side, and
 * a caption inside it would shrink to about seven pixels and turn with it, so
 * that view draws the caption in its footer instead, with `alone`: no margin
 * above it and set from the left, as the lines under it are. Under a chart the
 * markup is what it always was.
 */
export function SelectionCaption({ selected, alone = false }: { selected: string[]; alone?: boolean }) {
  return (
    <div className={alone ? `${styles.selection} ${styles.alone}` : styles.selection}>
      {selected.length === 0 ? (
        strings.odontogram.none
      ) : (
        <>
          <b>{sortTeeth(selected).join(', ')}</b> ·{' '}
          <span>
            {selected.length === 1
              ? strings.odontogram.oneSelected
              : `${selected.length} ${strings.odontogram.manySelected}`}
          </span>
        </>
      )}
    </div>
  );
}

interface DrawProps {
  chosen: Set<string>;
  pickable: boolean;
  onToggle: (tooth: string) => void;
  spans: readonly Span[];
  typeNames: boolean;
}

/* ---------------------------------------------------------------- desktop */

/** The left edge of a tooth's cell on the desktop arch. */
function cellX(index: number): number {
  return SIDE + index * CELL + (index >= 8 ? MIDLINE_GAP : 0);
}

function ArchSvg({ arch, chosen, pickable, onToggle, spans, typeNames }: DrawProps & { arch: Arch }) {
  const teeth: readonly ToothNumber[] = arch === 'upper' ? ARCH_UPPER : ARCH_LOWER;
  const layout = layoutOf(arch === 'upper' ? UPPER_GROUPS : LOWER_GROUPS, typeNames);
  const sections = SECTIONS.filter((section) => archOf(section.teeth[0] as ToothNumber) === arch);
  const teethTop = layout.top.teeth ?? 0;

  return (
    <svg
      className={styles.arch}
      viewBox={`0 0 ${ARCH_WIDTH} ${layout.height}`}
      style={{ aspectRatio: `${ARCH_WIDTH} / ${layout.height}` }}
    >
      {typeNames &&
        sections.map((section, sectionIndex) => (
          <TypeBrackets
            key={section.key}
            section={section}
            offset={sectionIndex * 8}
            layout={layout}
            x={cellX}
            width={CELL}
          />
        ))}

      {sections.map((section, sectionIndex) => (
        <SectionBracket
          key={section.key}
          section={section}
          x1={cellX(sectionIndex * 8)}
          x2={cellX(sectionIndex * 8 + 7) + CELL}
          layout={layout}
        />
      ))}

      {spans.map((span) => {
        const covered = spanCovers(span);
        const first = teeth.indexOf(covered[0] as ToothNumber);
        const last = teeth.indexOf(covered[covered.length - 1] as ToothNumber);
        if (first < 0 || last < 0) return null;
        return (
          <SpanMark
            key={span.id}
            span={span}
            x1={cellX(first) + SPAN_INSET}
            x2={cellX(last) + CELL - SPAN_INSET}
            layout={layout}
          />
        );
      })}

      {teeth.map((tooth, index) => (
        <Tooth
          key={tooth}
          tooth={tooth}
          x={cellX(index)}
          y={teethTop}
          cellWidth={CELL}
          numberY={baseline(layout, 'numbers')}
          flipped={arch === 'lower'}
          on={chosen.has(tooth)}
          pickable={pickable}
          onToggle={onToggle}
        />
      ))}
    </svg>
  );
}

/** MOLAR / PREMOLAR / CANINE / INCISORS over their teeth, one set per section. */
function TypeBrackets({
  section,
  offset,
  layout,
  x,
  width,
}: {
  section: Section;
  offset: number;
  layout: Layout;
  x: (index: number) => number;
  width: number;
}) {
  const bracketTop = layout.top.typeBrackets ?? 0;
  const teethTop = layout.top.teeth ?? 0;
  const above = bracketTop < teethTop;
  const textY = baseline(layout, 'typeNames');

  return (
    <g>
      {TYPE_GROUPS.map((group) => {
        const indexes = section.teeth
          .map((tooth, index) => (group.glyphs.includes(toothGlyphOf(tooth)) ? index : -1))
          .filter((index) => index >= 0);
        const x1 = x(offset + (indexes[0] as number)) + 2;
        const x2 = x(offset + (indexes[indexes.length - 1] as number)) + width - 2;
        return (
          <g key={group.label}>
            <path className={styles.bracket} d={bracketPath(x1, x2, bracketTop, above)} />
            <text className={styles.typeLabel} x={(x1 + x2) / 2} y={textY}>
              {group.label}
            </text>
          </g>
        );
      })}
    </g>
  );
}

/** A thin bracket with 5px end ticks turned toward the teeth. */
function bracketPath(x1: number, x2: number, top: number, above: boolean): string {
  return above
    ? `M${x1} ${top + BRACKET_TICK} V${top} H${x2} V${top + BRACKET_TICK}`
    : `M${x1} ${top} V${top + BRACKET_TICK} H${x2} V${top}`;
}

function SectionBracket({
  section,
  x1,
  x2,
  layout,
}: {
  section: Section;
  x1: number;
  x2: number;
  layout: Layout;
}) {
  const top = layout.top.sectionBrackets ?? 0;
  const above = top < (layout.top.teeth ?? 0);
  return (
    <g>
      <path className={styles.bracket} d={bracketPath(x1 + 2, x2 - 2, top, above)} />
      <text className={styles.sectionLabel} x={(x1 + x2) / 2} y={baseline(layout, 'sectionLabels')}>
        {section.label}
      </text>
    </g>
  );
}

/**
 * One bridge or bar. The bridge is a 2px line with 8px ticks down at both
 * ends, nearest the teeth; the bar is a 4px rounded rule on the level above.
 * A span cut at a row's edge on the phone loses the tick on that side.
 */
function SpanMark({
  span,
  x1,
  x2,
  layout,
  openStart = false,
  openEnd = false,
}: {
  span: Span;
  x1: number;
  x2: number;
  layout: Layout;
  openStart?: boolean;
  openEnd?: boolean;
}) {
  const label = `${span.name}, ${span.from} ${strings.odontogram.to} ${span.to}`;
  const mid = (x1 + x2) / 2;

  if (span.kind === 'titaniumBar') {
    const top = layout.top.barLine ?? 0;
    return (
      <g className={styles.bar} role="img" aria-label={label}>
        <text className={styles.barName} x={mid} y={baseline(layout, 'barLabel')}>
          {span.name} · {SPAN_KIND_LABEL.titaniumBar}
        </text>
        <rect x={x1} y={top} width={x2 - x1} height={BAND_HEIGHT.barLine} rx={2} />
      </g>
    );
  }

  const y = (layout.top.bridgeLine ?? 0) + 1;
  const tickTo = y + BRIDGE_TICK;
  return (
    <g className={styles.bridge} role="img" aria-label={label}>
      <text className={styles.bridgeName} x={mid} y={baseline(layout, 'bridgeLabel')}>
        {span.name}
      </text>
      <path
        d={
          `${openStart ? `M${x1} ${y}` : `M${x1} ${tickTo} V${y}`} H${x2}` +
          (openEnd ? '' : ` V${tickTo}`)
        }
      />
    </g>
  );
}

function Tooth({
  tooth,
  x,
  y,
  cellWidth,
  numberY,
  flipped,
  on,
  pickable,
  onToggle,
}: {
  tooth: ToothNumber;
  x: number;
  y: number;
  cellWidth: number;
  numberY: number;
  flipped: boolean;
  on: boolean;
  pickable: boolean;
  onToggle: (tooth: string) => void;
}) {
  const glyph = GLYPH[toothGlyphOf(tooth)];
  const glyphX = x + (cellWidth - GLYPH_WIDTH) / 2;
  const centre = x + cellWidth / 2;

  const shape = (
    <g
      transform={
        `translate(${glyphX},${y})` +
        (flipped ? ` rotate(180 ${GLYPH_WIDTH / 2} ${GLYPH_HEIGHT / 2})` : '')
      }
    >
      <path className={styles.shape} d={glyph.roots} />
      <path className={styles.shape} d={glyph.crown} />
      {glyph.fissure !== null && <path className={styles.fissure} d={glyph.fissure} />}
    </g>
  );

  /*
   * The number is a SIBLING of the interactive group, not a child of it.
   *
   * A group's bounding box covers everything in it, so a number inside the
   * group drags the group's centre off the tooth, and a click aimed at the
   * middle of a tooth can land on the background instead. The end to end
   * suite caught it on the old chart. Nothing is lost by moving it out: the
   * accessible name is on the group already, spelled out as "Tooth 12".
   */
  const number = (
    <text className={`${styles.number} ${on ? styles.numberOn : ''}`} x={centre} y={numberY}>
      {tooth}
    </text>
  );

  if (!pickable) {
    return (
      <>
        <g className={`${styles.tooth} ${on ? styles.on : ''}`}>{shape}</g>
        {number}
      </>
    );
  }

  return (
    <>
      <g
        className={`${styles.tooth} ${on ? styles.on : ''}`}
        role="checkbox"
        aria-checked={on}
        aria-label={`${strings.odontogram.toothLabel} ${tooth}`}
        tabIndex={0}
        onClick={() => onToggle(tooth)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle(tooth);
          }
        }}
      >
        {/* The whole cell is the target, glyph and number together. */}
        <rect className={styles.target} x={x} y={y - 2} width={cellWidth} height={numberY - y + 6} />
        {shape}
      </g>
      {number}
    </>
  );
}

/* ---------------------------------------------------------------- phone */

function rowX(index: number): number {
  return ROW_SIDE + index * ROW_CELL;
}

/**
 * One section as a row of eight, so every tooth keeps a 38px target. A span
 * that crosses the midline is drawn on both rows it covers, running to the
 * row's edge on the joined side with no end tick there, and named on both.
 */
function SectionRow({ section, chosen, pickable, onToggle, spans }: DrawProps & { section: Section }) {
  const layout = layoutOf(ROW_GROUPS, false);
  const arch = archOf(section.teeth[0] as ToothNumber);
  const teethTop = layout.top.teeth ?? 0;
  // The joined side is where this row meets the other section of its arch.
  const joinedOnRight = section.key === 'ur' || section.key === 'lr';

  return (
    <div className={styles.row}>
      <div className={styles.rowHead}>
        <span className={styles.rowLabel}>{section.label}</span>
        <span className={styles.rowRule} />
      </div>
      <svg
        className={styles.rowSvg}
        viewBox={`0 0 ${ROW_WIDTH} ${layout.height}`}
        style={{ aspectRatio: `${ROW_WIDTH} / ${layout.height}` }}
      >
        {spans.map((span) => {
          const covered = spanCovers(span);
          const here = covered.filter((tooth) => section.teeth.includes(tooth));
          if (here.length === 0) return null;
          const first = section.teeth.indexOf(here[0] as ToothNumber);
          const last = section.teeth.indexOf(here[here.length - 1] as ToothNumber);
          const continues = here.length < covered.length;
          const openStart = continues && !joinedOnRight;
          const openEnd = continues && joinedOnRight;
          return (
            <SpanMark
              key={span.id}
              span={span}
              x1={openStart ? 0 : rowX(first) + SPAN_INSET}
              x2={openEnd ? ROW_WIDTH : rowX(last) + ROW_CELL - SPAN_INSET}
              layout={layout}
              openStart={openStart}
              openEnd={openEnd}
            />
          );
        })}

        {section.teeth.map((tooth, index) => (
          <Tooth
            key={tooth}
            tooth={tooth}
            x={rowX(index)}
            y={teethTop}
            cellWidth={ROW_CELL}
            numberY={baseline(layout, 'numbers')}
            flipped={arch === 'lower'}
            on={chosen.has(tooth)}
            pickable={pickable}
            onToggle={onToggle}
          />
        ))}
      </svg>
    </div>
  );
}
